import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { platformLogoSrc, STATIC_PLATFORM_LOGO } from '@/lib/platformBranding';

// Minimal, super-admin-only control for the CENTRAL SmartoERP platform
// identity (app name + logo). Deliberately excludes company fields (CR, VAT,
// address) — those belong to Company Profile. Reuses /platform-branding APIs.
export function PlatformBrandingView() {
  const [brand, setBrand] = useState({ app_name: 'SmartoERP', logo_url: '' });
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/platform-branding/public').then(({ data }) => {
      if (data) { setBrand(data); setName(data.app_name || ''); }
    }).catch(() => {});
  }, []);

  const saveName = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/platform-branding', { app_name: name.trim() });
      setBrand(data);
      toast.success('Platform name updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/platform-branding/upload-logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBrand((b) => ({ ...b, logo_url: data.logo_url }));
      toast.success('Platform logo updated');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card className="border-border max-w-xl" data-testid="platform-branding-view">
      <CardContent className="p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold font-outfit">Platform Branding</h2>
          <p className="text-sm text-muted-foreground">
            SmartoERP identity shown on the web login, Cashier &amp; Waiter sign-in screens. This is the software platform — not a company. Company details live in Company Profile.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Platform Logo</Label>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-xl border bg-muted flex items-center justify-center overflow-hidden">
              <img
                src={brand.logo_url ? platformLogoSrc(brand) : STATIC_PLATFORM_LOGO}
                alt="Platform logo"
                className="h-full w-full object-contain"
                data-testid="platform-logo-preview"
                onError={(ev) => { ev.currentTarget.style.display = 'none'; }}
              />
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickLogo} data-testid="platform-logo-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="platform-logo-upload-btn">
              {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
              {uploading ? 'Uploading…' : 'Upload Logo'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <ImageIcon size={12} /> PNG/JPG/WebP, max 5MB. Falls back to the built-in SmartoERP logo if unset.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="platform-app-name">Platform / App Name</Label>
          <div className="flex gap-2">
            <Input
              id="platform-app-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="SmartoERP"
              data-testid="platform-name-input"
            />
            <Button onClick={saveName} disabled={saving || !name.trim()} data-testid="platform-name-save-btn">
              {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
